import {
    faArrowLeft,
    faArrowsAltH,
    faCopy,
    faEraser,
    faExclamationTriangle,
    faFileImport,
    faFont,
    faPaste,
    faSave,
    faShareAlt,
    faUndoAlt,
    faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { Button, ButtonGroup, Divider, Input, InputGroup, Message, Panel, SelectPicker } from 'rsuite';
import { PictoValueInterface } from '..';
import { IpCan } from '../../../../handlers/ipcan/IpCan';
import { LPDisplay } from '../../../../handlers/lpDisplays/LPDisplay';
import { LP_MATRIXDISPLAYV2_PICTO_TYPE, PixelArtPictoImport } from '../../../../handlers/pixelArt/pixelArtWS';
import PanelHeader from '../../../Custom/PanelHeader';
import { downloadFile, selectPictoSizeData } from '../../../PixelArtFontEditor/RightSideBar/utils';
import { ImportPictoModal } from './ImportPictoModal';
import { ResetPictoModal } from './ResetPictoModal';
import { SavePictoModal } from './SavePictoModal';
import { SendPictoToManyDisplayModal } from './SendPictoToManyDisplayModal';

type Props = {
    pictoType: number;
    pictos: PictoValueInterface[];
    pictosFromApi: any;
    selectedPictoIndex: number;
    isLoading: boolean;
    handlePictoWidthChange: (value: number) => void;
    handlePictoUpload: (receivedPicto: PixelArtPictoImport) => void;
    readPictoFromAPI: () => void;
    handleSizeChange: (value: number) => void;
    handleSavePicto: () => void;
    handleSaveToManyPicto: (displays: number[]) => void;
    handleClickCopy: () => void;
    handleClickPaste: () => void;
    handleClickReset: () => void;
    isPasteButtonEnabled: boolean;
    display?: LPDisplay;
    ipcan?: IpCan;
};

export const RightSideBar = (props: Props) => {
    const intl = useIntl();

    const [importModalIsOpen, setImportModalIsOpen] = React.useState<boolean>(false);

    const [sendToManyModalIsOpen, setSendToManyModalIsOpen] = React.useState<boolean>(false);

    const [isResetModalOpen, setIsResetModalOpen] = React.useState<boolean>(false);

    const [saveModalIsOpen, setSaveModalIsOpen] = React.useState<boolean>(false);

    const currentPicto: PictoValueInterface | undefined = props.pictos[props.selectedPictoIndex];

    const history = useHistory();

    const handleClickSave = () => {
        let pictoType = '';

        if (props.pictoType === LP_MATRIXDISPLAYV2_PICTO_TYPE.PICTO_SIZE_16) {
            pictoType = '16';
        } else if (props.pictoType === LP_MATRIXDISPLAYV2_PICTO_TYPE.PICTO_SIZE_32) {
            pictoType = '32';
        }

        downloadFile(
            JSON.stringify({
                picto: props.pictosFromApi,
                pictoType: props.pictoType,
            }),
            `export-picto-${pictoType}px-${moment().format('DD_MM_YYYY-HH_mm_ss')}.json`
        );
    };

    const handleClickImport = () => {
        setImportModalIsOpen(true);
    };

    const handleChange = value => {
        props.handlePictoWidthChange(value);
    };

    const handleClickReset = () => {
        setIsResetModalOpen(true);
    };

    const handleSizeChange = (value: number) => {
        props.handleSizeChange(value);
    };

    return (
        <>
            <ImportPictoModal
                isOpen={importModalIsOpen}
                onHide={() => setImportModalIsOpen(false)}
                handlePictoUpload={props.handlePictoUpload}
            />

            <ResetPictoModal
                isOpen={isResetModalOpen}
                onHide={() => setIsResetModalOpen(false)}
                handleClickReset={() => props.readPictoFromAPI()}
            />

            <SendPictoToManyDisplayModal
                isOpen={sendToManyModalIsOpen}
                onHide={() => setSendToManyModalIsOpen(false)}
                handleSaveToManyPicto={props.handleSaveToManyPicto}
            />

            <SavePictoModal
                isOpen={saveModalIsOpen}
                onHide={() => setSaveModalIsOpen(false)}
                handleSavePicto={() => props.handleSavePicto()}
            />

            <Panel
                className="panel-small"
                bordered
                shaded
                header={
                    <PanelHeader
                        title={intl.formatMessage({ id: 'editPicto.editorMenu' })}
                        buttons={[
                            <Button
                                color="blue"
                                size="sm"
                                onClick={() => {
                                    if (props.ipcan && props.display) {
                                        return history.push(`/ipCan/${props.ipcan.getId()}/bus/${props.display.bus}`);
                                    } else {
                                        return history.push('/ipCan');
                                    }
                                }}>
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Button>,
                        ]}
                    />
                }>
                {(props.ipcan || props.display) && (
                    <>
                        <h5 className="text-center">
                            {props.ipcan?.getLabel()} - {props.display?.getBus() && props.display?.getBus()} -{' '}
                            {props.display?.getDeviceId()}
                        </h5>

                        <Divider className="margin-top-10" />
                    </>
                )}
                <ButtonGroup justified>
                    <Button onClick={handleClickReset} color="red" disabled={props.isLoading || !props.display}>
                        <FontAwesomeIcon icon={faUndoAlt} />
                    </Button>
                    <Button color="orange" disabled={props.isLoading} onClick={handleClickImport}>
                        <FontAwesomeIcon icon={faFileImport} />
                    </Button>
                    <Button
                        color="green"
                        disabled={props.isLoading || props.pictos.length === 0}
                        onClick={handleClickSave}>
                        <FontAwesomeIcon icon={faSave} />
                    </Button>
                </ButtonGroup>
                <Divider />
                <ButtonGroup justified>
                    <Button onClick={props.handleClickCopy} color="blue" disabled={props.isLoading}>
                        <FontAwesomeIcon icon={faCopy} />
                    </Button>
                    <Button
                        color="orange"
                        disabled={props.isLoading || !props.isPasteButtonEnabled}
                        onClick={props.handleClickPaste}>
                        <FontAwesomeIcon icon={faPaste} />
                    </Button>
                    <Button color="red" disabled={props.isLoading} onClick={props.handleClickReset}>
                        <FontAwesomeIcon icon={faEraser} />
                    </Button>
                </ButtonGroup>
                <Divider />
                <div className="margin-bottom-15">
                    <p className="bold">
                        <FormattedMessage id="editPicto.pictoType" />
                    </p>
                    <SelectPicker
                        disabled={props.isLoading}
                        value={props.pictoType}
                        data={selectPictoSizeData(intl)}
                        onChange={handleSizeChange}
                        searchable={false}
                        cleanable={false}
                    />
                </div>
                <div>
                    <InputGroup>
                        <InputGroup.Addon>
                            <FontAwesomeIcon icon={faArrowsAltH} />
                        </InputGroup.Addon>
                        <Input
                            type="number"
                            key={`${new Date()}-${props.selectedPictoIndex}`}
                            disabled={props.isLoading}
                            defaultValue={currentPicto?.value.length.toString() || '0'}
                            step={1}
                            max={100}
                            onChange={value => handleChange(parseInt(value))}
                        />
                    </InputGroup>
                </div>
                <Divider />
                {!props.display && (
                    <Message
                        className="margin-bottom-10"
                        type="warning"
                        description={
                            <>
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    color="orange"
                                    className="margin-right-5"
                                />{' '}
                                <FormattedMessage id="editPicto.noDisplay" />
                            </>
                        }></Message>
                )}
                <ButtonGroup justified>
                    <Button
                        onClick={() => setSaveModalIsOpen(true)}
                        disabled={props.isLoading || !props.display}
                        color="blue">
                        <FontAwesomeIcon icon={faUpload} />
                    </Button>
                    <Button onClick={() => setSendToManyModalIsOpen(true)} disabled={props.isLoading} color="violet">
                        <FontAwesomeIcon icon={faShareAlt} />
                    </Button>
                </ButtonGroup>

                <Divider />
                <Button
                    onClick={() =>
                        history.push(
                            `/ipCan/${props.ipcan?.getId()}/bus/${props.display?.getBus()}/matrix-display/${props.display?.getId()}/font/16`
                        )
                    }
                    block
                    appearance="ghost">
                    <FontAwesomeIcon icon={faFont} className="margin-right-5" />
                    <FormattedMessage id="editFont.switchToFont" />
                </Button>
            </Panel>
        </>
    );
};
